import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Inbox } from 'lucide-react'
import {
  emailOutboxApi,
  type OutboxMailDetail,
  type OutboxMailItem,
  type OutboxMailsParams,
  type OutboxPeopleParams,
  type OutboxPerson,
  type OutboxPersonMails,
} from '@/services/api/emailOutbox'
import { Modal } from '@/components/common/Modal'
import { EmailHtmlPreview } from '@/components/email-templates/EmailHtmlPreview'
import { LetterGlasDirectory } from '@/components/ui/LetterGlasDirectory'
import { extractErrorMessage } from '@/utils/errorHandler'

const STORAGE_KEY = 'outbox.eigeneSicht'

const ROLES = [
  { id: 'teacher', label: 'Lehrperson' },
  { id: 'director', label: 'Direktor' },
  { id: 'admin', label: 'Admin' },
  { id: 'hr', label: 'HR' },
  { id: 'engineer', label: 'Engineer' },
] as const

const STATUSES = [
  { id: 'FAILED', label: 'FAILED', tone: 'bad' as const },
  { id: 'PENDING', label: 'PENDING', tone: 'warn' as const },
  { id: 'SENT', label: 'SENT', tone: 'ok' as const },
]

const THEMES = [
  { id: 'login', label: 'Login' },
  { id: 'vertretung', label: 'Vertretung' },
  { id: 'erinnerung', label: 'Erinnerung' },
  { id: 'bestaetigung', label: 'Bestätigung' },
  { id: 'prep', label: 'Prep' },
  { id: 'alarm', label: 'Alarm' },
  { id: 'sonstiges', label: 'Sonstiges' },
]

const TIMES = [
  { id: 'heute', label: 'Heute' },
  { id: '7d', label: '7 Tage' },
  { id: '30d', label: '30 Tage' },
] as const

type TimeWindow = 'heute' | '7d' | '30d' | null
type PeopleFilter = { roles: string[]; statuses: string[]; auffaellig: boolean; since: TimeWindow }
type MailFilter = { statuses: string[]; themes: string[]; since: TimeWindow }
type ViewPair = { people: PeopleFilter; mails: MailFilter }

const EMPTY_PEOPLE: PeopleFilter = { roles: [], statuses: [], auffaellig: false, since: null }
const EMPTY_MAILS: MailFilter = { statuses: [], themes: [], since: null }

const PRESETS: { id: string; label: string; pair: ViewPair }[] = [
  { id: 'alle', label: 'Alle', pair: { people: EMPTY_PEOPLE, mails: EMPTY_MAILS } },
  {
    id: 'auffaellig',
    label: 'Auffällig',
    pair: {
      people: { roles: [], statuses: [], auffaellig: true, since: null },
      mails: { statuses: ['FAILED', 'PENDING'], themes: [], since: null },
    },
  },
  {
    id: 'failed-heute',
    label: 'FAILED heute',
    pair: {
      people: { roles: [], statuses: ['FAILED'], auffaellig: false, since: 'heute' },
      mails: { statuses: ['FAILED'], themes: [], since: 'heute' },
    },
  },
  {
    id: 'login-lehrer',
    label: 'Login · Lehrperson',
    pair: {
      people: { roles: ['teacher'], statuses: [], auffaellig: false, since: null },
      mails: { statuses: [], themes: ['login'], since: null },
    },
  },
]

function sameSet(a: string[], b: string[]) {
  if (a.length !== b.length) return false
  const right = new Set(b)
  return a.every(item => right.has(item))
}

function samePair(a: ViewPair, b: ViewPair) {
  return (
    sameSet(a.people.roles, b.people.roles)
    && sameSet(a.people.statuses, b.people.statuses)
    && a.people.auffaellig === b.people.auffaellig
    && a.people.since === b.people.since
    && sameSet(a.mails.statuses, b.mails.statuses)
    && sameSet(a.mails.themes, b.mails.themes)
    && a.mails.since === b.mails.since
  )
}

function toggleValue(list: string[], value: string) {
  return list.includes(value) ? list.filter(item => item !== value) : [...list, value]
}

function isTime(value: unknown): value is Exclude<TimeWindow, null> {
  return value === 'heute' || value === '7d' || value === '30d'
}

function loadEigene(): ViewPair | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { people?: Partial<PeopleFilter>; mails?: Partial<MailFilter> }
    const roles = Array.isArray(parsed.people?.roles)
      ? parsed.people.roles.filter((role): role is string => ROLES.some(item => item.id === role))
      : []
    const peopleStatuses = Array.isArray(parsed.people?.statuses)
      ? parsed.people.statuses.filter((status): status is string => STATUSES.some(item => item.id === status))
      : []
    const statuses = Array.isArray(parsed.mails?.statuses)
      ? parsed.mails.statuses.filter((status): status is string => STATUSES.some(item => item.id === status))
      : []
    const themes = Array.isArray(parsed.mails?.themes)
      ? parsed.mails.themes.filter((theme): theme is string => THEMES.some(item => item.id === theme))
      : []
    return {
      people: {
        roles,
        statuses: peopleStatuses,
        auffaellig: Boolean(parsed.people?.auffaellig),
        since: isTime(parsed.people?.since) ? parsed.people.since : null,
      },
      mails: {
        statuses,
        themes,
        since: isTime(parsed.mails?.since) ? parsed.mails.since : null,
      },
    }
  } catch {
    return null
  }
}

function peopleParams(q: string, filter: PeopleFilter): OutboxPeopleParams {
  const params: OutboxPeopleParams = {}
  if (q) params.q = q
  if (filter.roles.length) params.role = filter.roles.join(',')
  if (filter.statuses.length) params.status = filter.statuses.join(',')
  if (filter.auffaellig) params.auffaellig = true
  if (filter.since) params.since = filter.since
  return params
}

function mailParams(filter: MailFilter): OutboxMailsParams {
  const params: OutboxMailsParams = {}
  if (filter.statuses.length) params.status = filter.statuses.join(',')
  if (filter.themes.length) params.theme = filter.themes.join(',')
  if (filter.since) params.since = filter.since
  return params
}

function formatAt(value: string | null) {
  if (!value) return ''
  try {
    return new Date(value).toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return value
  }
}

function statusClass(status: string) {
  if (status === 'SENT') return 'bg-emerald-50 text-emerald-700'
  if (status === 'FAILED') return 'bg-red-50 text-red-700'
  return 'bg-amber-50 text-amber-800'
}

function pillClass(on: boolean, tone?: 'bad' | 'warn' | 'ok') {
  if (!on) return 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
  if (tone === 'bad') return 'border-red-700 bg-red-700 text-white'
  if (tone === 'warn') return 'border-amber-700 bg-amber-700 text-white'
  if (tone === 'ok') return 'border-emerald-700 bg-emerald-700 text-white'
  return 'border-gray-900 bg-gray-900 text-white'
}

function Pill({
  on,
  tone,
  children,
  onClick,
  label,
}: {
  on: boolean
  tone?: 'bad' | 'warn' | 'ok'
  children: string
  onClick: () => void
  label?: string
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={label}
      onClick={onClick}
      className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${pillClass(on, tone)}`}
    >
      {children}
    </button>
  )
}

export function SuperadminEmailOutboxPage() {
  const [people, setPeople] = useState<OutboxPerson[]>([])
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [activeView, setActiveView] = useState('alle')
  const [eigene, setEigene] = useState<ViewPair | null>(() => loadEigene())
  const [peopleFilter, setPeopleFilter] = useState<PeopleFilter>(EMPTY_PEOPLE)
  const [mailFilter, setMailFilter] = useState<MailFilter>(EMPTY_MAILS)
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null)
  const [personMails, setPersonMails] = useState<OutboxPersonMails | null>(null)
  const [openThemes, setOpenThemes] = useState<Record<string, boolean>>({})
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<OutboxMailDetail | null>(null)
  const [loadingPeople, setLoadingPeople] = useState(true)
  const [loadingMails, setLoadingMails] = useState(false)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const expected = useMemo(() => {
    if (activeView === 'eigene') return eigene ?? { people: EMPTY_PEOPLE, mails: EMPTY_MAILS }
    return PRESETS.find(preset => preset.id === activeView)?.pair ?? { people: EMPTY_PEOPLE, mails: EMPTY_MAILS }
  }, [activeView, eigene])

  const dirty = !samePair({ people: peopleFilter, mails: mailFilter }, expected)

  const applyPair = useCallback((id: string, pair: ViewPair) => {
    setActiveView(id)
    setPeopleFilter(pair.people)
    setMailFilter(pair.mails)
  }, [])

  const loadPeople = useCallback(async (query: string, filter: PeopleFilter) => {
    setLoadingPeople(true)
    try {
      const res = await emailOutboxApi.listPeople(peopleParams(query, filter))
      setPeople(res.people)
      setError(null)
      setSelectedEmail(current => {
        if (current && res.people.some(person => person.email === current)) return current
        return res.people[0]?.email ?? null
      })
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Postausgang konnte nicht geladen werden' }))
    } finally {
      setLoadingPeople(false)
    }
  }, [])

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [q])

  useEffect(() => {
    void loadPeople(debouncedQ, peopleFilter)
  }, [loadPeople, debouncedQ, peopleFilter])

  useEffect(() => {
    if (!selectedEmail) {
      setPersonMails(null)
      setDetail(null)
      setSelectedId(null)
      return
    }
    let cancelled = false
    setLoadingMails(true)
    setSelectedId(null)
    setDetail(null)
    emailOutboxApi.listMails(selectedEmail, mailParams(mailFilter))
      .then(res => {
        if (cancelled) return
        setPersonMails(res)
        const initialOpen: Record<string, boolean> = {}
        for (const theme of res.themes) {
          initialOpen[theme.id] = theme.id === 'login' || res.themes[0]?.id === theme.id
        }
        setOpenThemes(initialOpen)
        setError(null)
      })
      .catch(err => {
        if (!cancelled) {
          setError(extractErrorMessage(err, { loadError: 'Mails konnten nicht geladen werden' }))
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingMails(false)
      })
    return () => { cancelled = true }
  }, [selectedEmail, mailFilter])

  useEffect(() => {
    if (!selectedId) {
      setDetail(null)
      return
    }
    let cancelled = false
    setDetail(null)
    setLoadingDetail(true)
    emailOutboxApi.getMail(selectedId)
      .then(res => {
        if (!cancelled) {
          setDetail(res)
          setError(null)
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(extractErrorMessage(err, { loadError: 'Vorschau konnte nicht geladen werden' }))
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false)
      })
    return () => { cancelled = true }
  }, [selectedId])

  const selectedMail = useMemo(() => {
    if (!personMails || !selectedId) return null
    for (const theme of personMails.themes) {
      const item = theme.items.find(entry => entry.id === selectedId)
      if (item) return item
    }
    return null
  }, [personMails, selectedId])

  function selectView(id: string) {
    if (id === 'eigene') {
      if (!eigene) return
      applyPair(id, eigene)
      return
    }
    const preset = PRESETS.find(item => item.id === id)
    if (preset) applyPair(id, preset.pair)
  }

  function discard() {
    applyPair(activeView, expected)
  }

  function saveEigene() {
    const pair = { people: peopleFilter, mails: mailFilter }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pair))
    setEigene(pair)
    setActiveView('eigene')
  }

  function toggleTheme(id: string) {
    setOpenThemes(current => ({ ...current, [id]: !current[id] }))
  }

  function renderMailButton(item: OutboxMailItem) {
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => setSelectedId(item.id)}
        className={`w-full text-left rounded-lg border px-2.5 py-2 mb-1.5 ${
          selectedId === item.id ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 bg-white hover:bg-gray-50'
        }`}
      >
        <div className="flex justify-between gap-2">
          <span className="text-sm font-semibold text-gray-900">{item.subject}</span>
          <span className="text-xs text-gray-500 shrink-0">{formatAt(item.at)}</span>
        </div>
        <span className={`mt-1 inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${statusClass(item.status)}`}>
          {item.status}
        </span>
      </button>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="bg-white border-b px-4 py-2 flex items-center gap-2 shrink-0">
        <Inbox className="w-4 h-4 text-indigo-600" />
        <h1 className="text-sm font-semibold">
          Postausgang
          {personMails ? ` · ${personMails.recipient_name}` : ''}
        </h1>
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-800">Queue</span>
        {dirty && (
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">geändert</span>
        )}
        <span className="ml-auto text-xs text-gray-500">
          {loadingPeople ? '' : `${people.length} Personen`}
        </span>
      </div>
      <div className="flex border-b bg-gray-50 overflow-x-auto shrink-0">
        {PRESETS.map(preset => (
          <button
            key={preset.id}
            type="button"
            aria-pressed={activeView === preset.id}
            onClick={() => selectView(preset.id)}
            className={`px-3 py-2 text-xs whitespace-nowrap ${
              activeView === preset.id ? 'bg-white font-semibold text-gray-900 shadow-[inset_0_-2px_0_#4f46e5]' : 'text-gray-500'
            }`}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          aria-pressed={activeView === 'eigene'}
          disabled={!eigene}
          onClick={() => selectView('eigene')}
          className={`px-3 py-2 text-xs whitespace-nowrap disabled:opacity-40 ${
            activeView === 'eigene' ? 'bg-white font-semibold text-gray-900 shadow-[inset_0_-2px_0_#4f46e5]' : 'text-gray-500'
          }`}
        >
          Eigene Sicht
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 p-2 bg-gray-50 border-b shrink-0">
        <div className="bg-white border border-gray-200 rounded-lg p-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
            {peopleFilter.statuses.length ? 'Personen · Mail mit Status' : 'Personen · letzte Mail'}
          </div>
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Person suchen…"
            className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm mb-2"
          />
          <div className="flex flex-wrap gap-1">
            {ROLES.map(role => (
              <Pill
                key={role.id}
                on={peopleFilter.roles.includes(role.id)}
                onClick={() => setPeopleFilter(current => ({ ...current, roles: toggleValue(current.roles, role.id) }))}
              >
                {role.label}
              </Pill>
            ))}
            {STATUSES.map(status => (
              <Pill
                key={`person-${status.id}`}
                tone={status.tone}
                label={`Person hat ${status.label}`}
                on={peopleFilter.statuses.includes(status.id)}
                onClick={() => setPeopleFilter(current => ({ ...current, statuses: toggleValue(current.statuses, status.id) }))}
              >
                {status.label}
              </Pill>
            ))}
            <Pill
              tone="bad"
              on={peopleFilter.auffaellig}
              onClick={() => setPeopleFilter(current => ({ ...current, auffaellig: !current.auffaellig }))}
            >
              Nur auffällig
            </Pill>
            {TIMES.map(window => (
              <Pill
                key={window.id}
                on={peopleFilter.since === window.id}
                onClick={() => setPeopleFilter(current => ({
                  ...current,
                  since: current.since === window.id ? null : window.id,
                }))}
              >
                {window.label}
              </Pill>
            ))}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Mails · Mail-Datum</div>
          <div className="flex flex-wrap gap-1">
            {STATUSES.map(status => (
              <Pill
                key={status.id}
                tone={status.tone}
                on={mailFilter.statuses.includes(status.id)}
                onClick={() => setMailFilter(current => ({ ...current, statuses: toggleValue(current.statuses, status.id) }))}
              >
                {status.label}
              </Pill>
            ))}
            {THEMES.map(theme => (
              <Pill
                key={theme.id}
                on={mailFilter.themes.includes(theme.id)}
                onClick={() => setMailFilter(current => ({ ...current, themes: toggleValue(current.themes, theme.id) }))}
              >
                {theme.label}
              </Pill>
            ))}
            {TIMES.map(window => (
              <Pill
                key={`mail-${window.id}`}
                on={mailFilter.since === window.id}
                onClick={() => setMailFilter(current => ({
                  ...current,
                  since: current.since === window.id ? null : window.id,
                }))}
              >
                {window.label}
              </Pill>
            ))}
          </div>
        </div>
      </div>
      {dirty && (
        <div className="px-3 py-2 border-b bg-amber-50 flex items-center gap-2 shrink-0">
          <span className="text-xs text-amber-900">Sicht weicht ab</span>
          <button type="button" onClick={discard} className="ml-auto text-xs border border-gray-300 bg-white rounded-md px-2 py-1">
            Verwerfen
          </button>
          <button type="button" onClick={saveEigene} className="text-xs rounded-md px-2 py-1 bg-gray-900 text-white">
            Als eigene Sicht speichern
          </button>
        </div>
      )}
      {error && (
        <div className="mx-4 mt-3 p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>
      )}
      <div className="flex flex-1 min-h-0">
        <div className="w-80 border-r bg-white shrink-0 min-h-0 overflow-y-auto">
          <div className="p-2.5">
            <LetterGlasDirectory
              items={people}
              getText={person => person.name}
              getKey={person => person.email}
              selectedKey={selectedEmail}
              onSelect={person => setSelectedEmail(person.email)}
              empty={loadingPeople ? 'Laden...' : 'Keine Empfänger'}
              ariaLabelPrefix="Empfänger"
              renderRow={person => (
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-gray-900">
                    {person.name}
                    {person.teacher_code ? (
                      <span className="text-gray-400 text-xs font-medium ml-1.5">({person.teacher_code})</span>
                    ) : null}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{person.email}</div>
                  <div className="mt-1 text-xs text-gray-500">
                    {person.role_label && (
                      <span className="mr-1.5 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 text-[10px] font-medium">
                        {person.role_label}
                      </span>
                    )}
                    {person.mail_count} Mails
                    {person.login_count > 0 ? ` · ${person.login_count} Login` : ''}
                  </div>
                </div>
              )}
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {loadingMails ? (
            <div className="text-sm text-gray-500 p-6">Mails laden...</div>
          ) : !personMails ? (
            <div className="text-sm text-gray-400 p-6">Person wählen</div>
          ) : personMails.themes.length === 0 ? (
            <div className="text-sm text-gray-400 p-6">Keine Mails in dieser Sicht.</div>
          ) : (
            personMails.themes.map(theme => {
              const open = Boolean(openThemes[theme.id])
              return (
                <div key={theme.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-2">
                  <button
                    type="button"
                    onClick={() => toggleTheme(theme.id)}
                    className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold bg-slate-50 hover:bg-indigo-50"
                  >
                    <span className="flex items-center gap-1.5">
                      {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      {theme.name}
                    </span>
                    <span className="text-xs font-normal text-gray-500">{theme.items.length} · {theme.hint}</span>
                  </button>
                  {open && (
                    <div className="p-2">
                      {theme.items.map(renderMailButton)}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      </div>
      <Modal
        isOpen={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={detail?.subject || selectedMail?.subject || 'E-Mail'}
        size="xl"
      >
        {loadingDetail || (detail != null && detail.id !== selectedId) ? (
          <div className="text-sm text-gray-500">Vorschau laden...</div>
        ) : !detail ? (
          <div className="text-sm text-red-700">{error || 'Vorschau konnte nicht geladen werden'}</div>
        ) : (
          <>
            <div className="mb-3 text-sm text-gray-600">
              An: {detail.recipient_name ? `${detail.recipient_name} · ` : ''}{detail.recipient_email}
              {detail.at ? ` · ${formatAt(detail.at)}` : ''}
            </div>
            {detail.status !== 'SENT' && (
              <div className="mb-3 px-3 py-2 text-xs text-amber-800 bg-amber-50 rounded">
                {detail.status}
                {detail.last_error ? ` — ${detail.last_error}` : ''}
              </div>
            )}
            {detail.body_html ? (
              <EmailHtmlPreview html={detail.body_html} title={detail.subject} />
            ) : (
              <div className="text-sm text-gray-500">Keine HTML-Datei.</div>
            )}
          </>
        )}
      </Modal>
    </div>
  )
}
